/*
  # Fixed booking calendar date picker
  
  1. Changes
    - Fixed month navigation to properly show current and future months
    - Extended booking availability to 12 months in advance
    - Fixed calendar grid to show correct dates for each month
    - Improved date availability calculation
    - Added proper month/year display
*/

import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { Tour } from '../../types/tours';
import { formatDate, isValidBookingTime, getCostaRicaTime } from '../../utils/dateUtils';

interface BookingDateStepProps {
  tour: Tour;
  selectedDate: Date | null;
  onDateSelect: (date: Date) => void;
  onNext: () => void;
}

const BookingDateStep: React.FC<BookingDateStepProps> = ({
  tour,
  selectedDate,
  onDateSelect,
  onNext
}) => {
  const [currentMonth, setCurrentMonth] = useState(() => {
    // Get current time in Costa Rica using proper UTC offset
    const costaRicaNow = getCostaRicaTime();
    
    // Start with current month, but if no dates are available, move to next month
    let startMonth = new Date(costaRicaNow.getFullYear(), costaRicaNow.getMonth(), 1);
    
    // Check if current month has any available dates
    const [hours, minutes] = tour.departure.split(':').map(Number);
    let hasAvailableDates = false;
    
    for (let day = 1; day <= new Date(startMonth.getFullYear(), startMonth.getMonth() + 1, 0).getDate(); day++) {
      const testDate = new Date(startMonth.getFullYear(), startMonth.getMonth(), day);
      testDate.setHours(hours, minutes, 0, 0);
      
      if (isValidBookingTime(testDate, tour.departure)) {
        hasAvailableDates = true;
        break;
      }
    }
    
    // If no available dates in current month, move to next month
    if (!hasAvailableDates) {
      startMonth.setMonth(startMonth.getMonth() + 1);
    }
    
    return startMonth;
  });
  const [error, setError] = useState<string | null>(null);

  const getAvailableDatesForMonth = (month: Date): Date[] => {
    const dates: Date[] = [];
    const [hours, minutes] = tour.departure.split(':').map(Number);
    
    // Get current time in Costa Rica
    const costaRicaNow = getCostaRicaTime();
    
    // Get first and last day of the month
    const firstDay = new Date(month.getFullYear(), month.getMonth(), 1);
    const lastDay = new Date(month.getFullYear(), month.getMonth() + 1, 0);
    
    // Check each day in the month
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const date = new Date(month.getFullYear(), month.getMonth(), day);
      date.setHours(hours, minutes, 0, 0);
      
      // Only include dates that are valid for booking
      if (isValidBookingTime(date, tour.departure)) {
        dates.push(new Date(date));
      }
    }
    
    return dates;
  };

  const handleDateSelect = (date: Date) => {
    const [hours, minutes] = tour.departure.split(':').map(Number);
    const selectedDateTime = new Date(date);
    selectedDateTime.setHours(hours, minutes, 0, 0);

    if (!isValidBookingTime(selectedDateTime, tour.departure)) {
      setError('Must book at least 12 hours before departure');
      return;
    }

    setError(null);
    onDateSelect(selectedDateTime);
  };

  const handleNext = () => {
    if (!selectedDate) {
      setError('Please select a date');
      return;
    }
    onNext();
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay(); // 0 = Sunday, 1 = Monday, etc.
    
    return { daysInMonth, startingDay, firstDay, lastDay };
  };

  const { daysInMonth, startingDay } = getDaysInMonth(currentMonth);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const blanks = Array.from({ length: startingDay }, (_, i) => i);

  const availableDates = getAvailableDatesForMonth(currentMonth);

  const isDateAvailable = (day: number) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return availableDates.some(d => 
      d.getDate() === day && 
      d.getMonth() === currentMonth.getMonth() && 
      d.getFullYear() === currentMonth.getFullYear()
    );
  };

  const isDateSelected = (day: number) => {
    if (!selectedDate) return false;
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return selectedDate.getDate() === day &&
           selectedDate.getMonth() === currentMonth.getMonth() &&
           selectedDate.getFullYear() === currentMonth.getFullYear();
  };

  const canGoToPreviousMonth = () => {
    const costaRicaNow = getCostaRicaTime();
    const prevMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
    const currentMonthStart = new Date(costaRicaNow.getFullYear(), costaRicaNow.getMonth(), 1);
    return prevMonth >= currentMonthStart;
  };

  const canGoToNextMonth = () => {
    const costaRicaNow = getCostaRicaTime();
    const maxDate = new Date(costaRicaNow.getFullYear() + 1, costaRicaNow.getMonth(), 1);
    const nextMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
    return nextMonth < maxDate;
  };

  const goToPreviousMonth = () => {
    if (canGoToPreviousMonth()) {
      setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
    }
  };

  const goToNextMonth = () => {
    if (canGoToNextMonth()) {
      setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
    }
  };

  // Auto-select first available date if none is selected and we have available dates
  useEffect(() => {
    if (!selectedDate && availableDates.length > 0) {
      onDateSelect(availableDates[0]);
    }
  }, [currentMonth, selectedDate, availableDates.length, onDateSelect]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-[#2A382A] mb-2">Select a Date</h2>
        <p className="text-[#2A382A]/70">
          Choose your preferred date for the tour. You can book up to 12 months in advance. 
          Bookings must be made at least 12 hours before departure.
        </p>
      </div>

      {/* Calendar */}
      <div className="bg-[#F2F3F4] rounded-xl p-6">
        {/* Calendar Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={goToPreviousMonth}
            disabled={!canGoToPreviousMonth()}
            aria-label="Previous month"
            className={`p-2 rounded-lg transition-colors ${
              canGoToPreviousMonth() 
                ? 'hover:bg-white/50 text-[#2A382A]' 
                : 'text-[#2A382A]/30 cursor-not-allowed'
            }`}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h3 className="text-lg font-semibold text-[#2A382A]">
            {(() => {
              // Use the currentMonth state directly for header display
              const headerText = currentMonth.toLocaleDateString('en-US', {
                month: 'long',
                year: 'numeric'
              });
              console.log('🗓️ Calendar header display:', {
                currentMonth: currentMonth.toISOString(),
                headerText,
                currentMonthLocal: currentMonth.toString(),
                monthNumber: currentMonth.getMonth(),
                year: currentMonth.getFullYear()
              });
              return headerText;
            })()}
          </h3>
          <button
            onClick={goToNextMonth}
            disabled={!canGoToNextMonth()}
            aria-label="Next month"
            className={`p-2 rounded-lg transition-colors ${
              canGoToNextMonth() 
                ? 'hover:bg-white/50 text-[#2A382A]' 
                : 'text-[#2A382A]/30 cursor-not-allowed'
            }`}
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-2">
          {/* Day Labels */}
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <div key={day} className="text-center text-sm font-medium text-[#2A382A]/70 py-2">
              {day}
            </div>
          ))}

          {/* Blank Spaces for days before month starts */}
          {blanks.map((blank) => (
            <div key={`blank-${blank}`} className="w-10 h-10 flex items-center justify-center" />
          ))}

          {/* Days */}
          {days.map((day) => {
            const available = isDateAvailable(day);
            const selected = isDateSelected(day);

            return (
              <button
                key={day}
                onClick={() => {
                  if (available) {
                    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
                    handleDateSelect(date);
                  }
                }}
                aria-label={`Select ${new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'America/Costa_Rica' })}`}
                disabled={!available}
                className={`w-10 h-10 rounded-lg mx-auto flex items-center justify-center text-sm font-medium transition-all ${
                  available 
                    ? `${selected 
                        ? 'bg-[#2A382A] text-white shadow-lg scale-105' 
                        : 'bg-white text-[#2A382A] hover:bg-[#2A382A]/10 hover:scale-105 shadow-sm'
                      } cursor-pointer`
                    : 'bg-[#2A382A]/5 text-[#2A382A]/30 cursor-not-allowed relative'
                }`}
              >
                {day}
                {!available && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-full h-px bg-[#2A382A]/20 rotate-45" />
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Available dates info */}
        <div className="mt-4 text-center">
          <p className="text-sm text-[#2A382A]/60">
            {availableDates.length === 0 
              ? 'No available dates this month' 
              : `${availableDates.length} available ${availableDates.length === 1 ? 'date' : 'dates'} this month`
            }
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 text-red-500 p-4 rounded-lg">
          {error}
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-end">
        <button
          onClick={handleNext}
          className="px-6 py-2 bg-[#953B0E] text-white rounded-lg hover:bg-[#ff6a0d] transition-colors disabled:opacity-50 border-2 border-[#E85C0D]"
          disabled={!selectedDate}
        >
          Continue
        </button>
      </div>
    </div>
  );
};

export default BookingDateStep;